import type { ICellRendererParams } from '@ag-grid-community/core';
import type { MasterDetailConfig } from 'src/utils/Component/AgGrid/AgConfigParse';
import type { BasicTenantConfigViewItem } from 'src/dao/tenantConfigClient';
import type { BasicPivotItem } from 'src/worker/pivotWorker.types';
import type { EditableGridProps } from '../ConfigurableGrid/EditableGrid/EditableGrid';
import type { ConfigurableGridColumnDef } from '../ConfigurableGrid/ConfigurableGrid.types';
import type { AddBreadcrumbPayload } from 'src/state/breadcrumbs/Bread.slice';
import type { NavigateFunction } from 'react-router';
import type { TrendSummaryCardConfig } from 'configuration-schemas/viewdefns/viewdefn';

export enum MasterDetailRendererType {
  chart = 'chart',
  editableGrid = 'editableGrid',
  insights = 'insights',
}

export interface MasterDetailChartMetrics {
  left: BasicTenantConfigViewItem[];
  right?: BasicTenantConfigViewItem[];
}

interface MasterDetailBaseRendererConfig {
  title?: string;
}

export interface MasterDetailChartConfig extends MasterDetailBaseRendererConfig {
  renderer: MasterDetailRendererType.chart;
  chartTitle: string;
  xAxisDataIndex: string;
  metrics: MasterDetailChartMetrics;
}

interface MasterDetailInsightsMetrics {
  left: TrendSummaryCardConfig[];
  right: BasicTenantConfigViewItem[];
}

export interface MasterDetailInsightsConfig extends MasterDetailBaseRendererConfig {
  renderer: MasterDetailRendererType.insights;
  header: {
    id: string;
    text: string;
    icon: string;
    description: string;
  };
  metrics: MasterDetailInsightsMetrics;
}

interface MasterDetailInsightsMetrics {
  left: TrendSummaryCardConfig[];
  right: BasicTenantConfigViewItem[];
}

export interface MasterDetailInsightsConfig extends MasterDetailBaseRendererConfig {
  header: {
    id: string;
    text: string;
    icon: string;
    description: string;
  };
  metrics: MasterDetailInsightsMetrics;
}

export interface MasterDetailGridConfig
  extends Pick<
    EditableGridProps,
    | 'updateCoordinateMap'
    | 'massEditConfig'
    | 'idProp'
    | 'leafIdProp'
    | 'salesAdjustmentConfig'
    | 'clientActionHandlers'
    | 'onlySendCoordinateMap'
  > {
  renderer: MasterDetailRendererType.editableGrid;
  columns: ConfigurableGridColumnDef[];
}

export interface MasterDetailRendererProps extends ICellRendererParams, MasterDetailConfig {
  addCrumb(payload: AddBreadcrumbPayload): void;
  routerNavigate: NavigateFunction;
}

export interface MasterDetailRendererState {
  isLoading: boolean;
  config: MasterDetailChartConfig | MasterDetailInsightsConfig | MasterDetailGridConfig | null;
  data: BasicPivotItem[];
}
